import React, { useEffect, useRef, useState } from "react";
import Dashboard from "@uppy/react/dashboard";
import Uppy from "@uppy/core";
import AwsS3 from "@uppy/aws-s3";
import Italian from "@uppy/locales/lib/it_IT";
import "@uppy/core/css/style.min.css";
import "@uppy/dashboard/css/style.min.css";
import { useAuth0 } from "../../../react-auth0-spa";
import { useAppContext } from "../../../context";
import config from "../../../api_config.json";

const FileUploader = ({
  onComplete,
  onUploadStart,
  allowedFileTypes = [".pdf"],
  maxFileSize = 20 * 1024 * 1024,
  maxNumberOfFiles = 1,
  note = "Solo file PDF, max 20 MB",
  height = 200,
  collection = "ofc_tos",
  target = "files",
  path = "",
}) => {
  const uppyRef = useRef(null);
  const [isUppyReady, setIsUppyReady] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";

  const safeCloseUppy = () => {
    if (isUploading) return;

    if (uppyRef.current) {
      try {
        uppyRef.current.off("complete");
        uppyRef.current.off("upload-error");
        uppyRef.current.off("error");
        uppyRef.current.off("upload");

        const fileIDs = Object.keys(uppyRef.current.getState().files || {});
        fileIDs.forEach((id) => {
          try {
            uppyRef.current.removeFile(id);
          } catch (e) {
            // ignore
          }
        });

        if (typeof uppyRef.current.close === "function") {
          uppyRef.current.close();
        }
      } catch (e) {
        // ignore
      }
      uppyRef.current = null;
    }
    setIsUppyReady(false);
  };

  useEffect(() => {
    if (isUploading) return;
    safeCloseUppy();

    const initUppy = async () => {
      try {
        const token = await getTokenSilently();

        const uppyInstance = new Uppy({
          locale: Italian,
          autoProceed: true,
          restrictions: {
            maxFileSize,
            maxNumberOfFiles,
            minNumberOfFiles: 1,
            allowedFileTypes,
          },
          onBeforeFileAdded: (currentFile) => {
            currentFile.id = currentFile.name + "-" + Date.now();
            return true;
          },
        });

        const isCdn = target === "cdn";
        const uploadEndpoint = isCdn
          ? `${config.baseUrl[env]}/cdn-upload`
          : `${config.baseUrl[env]}/upload`;

        uppyInstance.use(AwsS3, {
          companionUrl: null,
          getUploadParameters(file) {
            const body = isCdn
              ? { filename: file.name, contentType: file.type, path }
              : { filename: file.name, contentType: file.type, collection };

            return fetch(uploadEndpoint, {
              method: "post",
              headers: {
                accept: "application/json",
                "content-type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify(body),
            })
              .then((response) => {
                if (!response.ok) {
                  throw new Error(`Server returned ${response.status}`);
                }
                return response.json();
              })
              .then((data) => {
                if (!data.result || !data.result.url) {
                  throw new Error("Invalid response from server");
                }
                return {
                  method: data.result.method,
                  url: data.result.url,
                  headers: {
                    "Content-Type": file.type,
                  },
                };
              });
          },
        });

        uppyInstance.on("upload", () => {
          setIsUploading(true);
          if (onUploadStart) onUploadStart();
        });

        uppyInstance.on("upload-error", (file, error) => {
          console.error("Upload error:", error);
          setIsUploading(false);
        });

        uppyInstance.on("complete", (result) => {
          setTimeout(() => setIsUploading(false), 500);

          if (onComplete && result.successful && result.successful.length > 0) {
            const uploaded = result.successful[0];
            const uploadUrl = uploaded.uploadURL || uploaded.response?.uploadURL;
            const s3Path = new URL(uploadUrl).pathname;
            const baseUrl = isCdn ? config.cdn[env] : config.files[env];
            const cdnUrl = `${baseUrl}${s3Path}`;
            const s3Filename = s3Path.substring(1);
            onComplete(cdnUrl, s3Filename);
          }
        });

        uppyInstance.on("error", (error) => {
          console.error("Uppy error:", error);
          setIsUploading(false);
        });

        uppyRef.current = uppyInstance;
        setIsUppyReady(true);
      } catch (error) {
        console.error("Error initializing uploader:", error);
        setIsUploading(false);
      }
    };

    initUppy();

    return () => {
      if (!isUploading) {
        safeCloseUppy();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof onComplete === "function" && uppyRef.current) {
      uppyRef.current.off("complete");
      uppyRef.current.on("complete", (result) => {
        setTimeout(() => setIsUploading(false), 500);

        if (result.successful && result.successful.length > 0) {
          const uploaded = result.successful[0];
          const uploadUrl = uploaded.uploadURL || uploaded.response?.uploadURL;
          const s3Path = new URL(uploadUrl).pathname;
          const baseUrl = target === "cdn" ? config.cdn[env] : config.files[env];
          const cdnUrl = `${baseUrl}${s3Path}`;
          const s3Filename = s3Path.substring(1);
          onComplete(cdnUrl, s3Filename);
        }
      });
    }
  }, [onComplete, env, target]);

  return (
    <div>
      {isUppyReady && (
        <Dashboard
          uppy={uppyRef.current}
          width="100%"
          height={height}
          showProgressDetails={true}
          note={note}
          proudlyDisplayPoweredByUppy={false}
          locale={{
            strings: {
              dropPasteFiles: "Trascina qui il file o %{browseFiles}",
              browseFiles: "sfoglia",
            },
          }}
          doneButtonHandler={null}
        />
      )}
    </div>
  );
};

export default FileUploader;
